// src/components/kpi/Kpi.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Toast, ToastContainer } from "react-bootstrap";

import KpiTrends from "./KpiTrends.jsx";
import KpiFilters from "./KpiFilters.jsx";
import KpiFillModal from "./KpiFillModal.jsx";
import KpiHelpModal from "./KpiHelpModal.jsx";
import "./KpiView.css";

const UI_ENTITIES = new Set(["GROUP", "FR", "DE", "BE", "CH", "US", "ES", "UK", "GR", "IT"]);

const normalizeUiEntity = (value) => {
  const v = String(value ?? "").trim().toUpperCase();
  if (v === "IE") return "UK";
  return UI_ENTITIES.has(v) ? v : "";
};

const getCurrentPeriod = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${d.getFullYear()}-${mm}`;
};

function kindUnit(kindRaw) {
  const kind = String(kindRaw || "").toLowerCase();
  if (kind === "hours") return "h";
  if (kind === "days") return "d";
  if (kind === "pct") return "%";
  return "";
}

function fmtNumberSafe(n, digits = 0) {
  if (!Number.isFinite(n)) return "";
  return String(parseFloat(n.toFixed(digits)));
}

export default function Kpi({ user }) {
  const userEntity = useMemo(() => normalizeUiEntity(user?.location), [user]);

  const [viewerEntity, setViewerEntity] = useState("");
  const [period, setPeriod] = useState(getCurrentPeriod);

  const [showFill, setShowFill] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ is_closed: false, period_exists: true });
  const [loading, setLoading] = useState(false);

  const [toast, setToast] = useState({ show: false, message: "", variant: "success" });
  const showToast = (message, variant = "success") => setToast({ show: true, message, variant });

  useEffect(() => {
    if (userEntity) setViewerEntity(userEntity);
  }, [userEntity]);

  const currentPeriod = useMemo(getCurrentPeriod, []);
  const isCurrentPeriod = period === currentPeriod;

  const canEdit =
    viewerEntity &&
    userEntity &&
    viewerEntity === userEntity &&
    isCurrentPeriod &&
    !meta?.is_closed;

  const fetchKpis = async () => {
    if (!viewerEntity) return;

    try {
      setLoading(true);

      const url = `/api/kpi.php?entity=${encodeURIComponent(viewerEntity)}&period=${encodeURIComponent(period)}`;
      const res = await fetch(url, { credentials: "include" });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.success) throw new Error(data?.error || `HTTP ${res.status}`);

      setRows(Array.isArray(data.data) ? data.data : []);
      setMeta(data.meta || { is_closed: false, period_exists: true });
    } catch (e) {
      console.error(e);
      setRows([]);
      setMeta({ is_closed: false, period_exists: true });
      showToast(e?.message || "Failed to load KPI", "danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (viewerEntity) fetchKpis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerEntity, period]);

  const handleSave = async (payload) => {
    if (!canEdit) {
      const msg = meta?.is_closed
        ? "This period is closed."
        : "Edit is allowed only for the current month and your own entity.";
      showToast(msg, "warning");
      return;
    }

    const res = await fetch("/api/kpi.php", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.success) throw new Error(data?.error || `HTTP ${res.status}`);

    showToast("KPI saved", "success");
    await fetchKpis();
  };

  const handleOpenFill = () => {
    if (!canEdit) {
      const msg = meta?.is_closed
        ? "This period is closed."
        : "Edit is allowed only for the current month and your own entity.";
      showToast(msg, "warning");
      return;
    }
    setShowFill(true);
  };

  const comment =
    rows?.map((r) => String(r?.comment ?? "").trim()).find((c) => c.length > 0) || "";

  const formatValue = (row) => {
    const v = row?.value;
    if (v === null || v === undefined || v === "") return "";

    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);

    const kind = String(row?.value_kind || "").toLowerCase();
    const unit = kindUnit(kind);

    if (kind === "pct") return `${fmtNumberSafe(n, 1)}%`;
    if (kind === "score") return fmtNumberSafe(n, 0);
    if (kind === "int") return String(parseInt(n, 10));
    if (kind === "days" || kind === "hours") return `${String(parseInt(n, 10))} ${unit}`;

    return fmtNumberSafe(n, 2);
  };

  const unitHint = (row) => {
    const u = kindUnit(row?.value_kind);
    if (!u || u === "%") return "";
    return u;
  };

  const grouped = useMemo(() => {
    const out = new Map();
    for (const r of rows || []) {
      const area = String(r?.board_area || r?.category || "KPIs");
      if (!out.has(area)) out.set(area, []);
      out.get(area).push(r);
    }

    const arr = Array.from(out.entries()).sort((a, b) => String(a[0]).localeCompare(String(b[0])));
    for (const [, items] of arr) {
      items.sort(
        (a, b) => Number(a?.board_sort ?? a?.sort_order ?? 0) - Number(b?.board_sort ?? b?.sort_order ?? 0)
      );
    }
    return arr;
  }, [rows]);

  return (
    <div className="kpi-wrapper mt-4">
      <KpiTrends country={viewerEntity} period={period} />

      <KpiFilters
        country={viewerEntity}
        setCountry={setViewerEntity}
        defaultCountry={userEntity}
        period={period}
        setPeriod={setPeriod}
        onOpenFill={handleOpenFill}
        onOpenHelp={() => setShowHelp(true)}
        onRefresh={fetchKpis}
        isEditablePeriod={isCurrentPeriod && !meta?.is_closed}
      />

      <div className="kpi-table-scroll">
        <table className="table table-dark table-bordered kpi-table">
          <colgroup>
            <col className="kpi-col-label" />
            <col className="kpi-col-value" />
            <col className="kpi-col-updated" />
            <col className="kpi-col-by" />
          </colgroup>

          <thead>
            <tr>
              <th>KPI</th>
              <th style={{ textAlign: "center" }}>Value</th>
              <th style={{ textAlign: "center" }}>Updated</th>
              <th style={{ textAlign: "center" }}>By</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="kpi-empty-cell">Loading...</td>
              </tr>
            ) : rows.length ? (
              grouped.flatMap(([area, items]) => [
                <tr key={`area-${area}`} className="kpi-section-row">
                  <td colSpan={4}>
                    <span className="kpi-section-title">{area}</span>
                  </td>
                </tr>,
                ...items.map((r) => (
                  <tr key={r.kpi_key} className="kpi-item-row">
                    <td className="kpi-label-cell">
                      <span className="kpi-label-main">{r.board_kpi_label || r.label || r.kpi_key}</span>
                      {!!unitHint(r) && <span className="kpi-label-unit">{unitHint(r)}</span>}
                    </td>
                    <td className="kpi-value-cell">{formatValue(r)}</td>
                    <td className="kpi-meta-cell">{r.updated_at ?? ""}</td>
                    <td className="kpi-meta-cell">{r.updated_by ?? ""}</td>
                  </tr>
                )),
              ])
            ) : (
              <tr>
                <td colSpan={4} className="kpi-empty-cell">No results found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!!comment && (
        <div className="kpi-card mt-3">
          <div className="kpi-card-title">Comment</div>
          <div className="kpi-card-body">{comment}</div>
        </div>
      )}

      <KpiFillModal
        show={showFill}
        onHide={() => setShowFill(false)}
        period={period}
        country={viewerEntity}
        onSave={handleSave}
        onToast={showToast}
      />

      <KpiHelpModal
        show={showHelp}
        onHide={() => setShowHelp(false)}
        entity={viewerEntity}
        period={period}
        rows={rows}
      />

      <ToastContainer
        position="bottom-end"
        className="p-3"
        style={{ position: "fixed", bottom: 20, right: 20, zIndex: 9999 }}
      >
        <Toast
          show={toast.show}
          bg={toast.variant}
          onClose={() => setToast((p) => ({ ...p, show: false }))}
          delay={3000}
          autohide
        >
          <Toast.Body className="text-white">{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
}